/**
 * `Raw*` types describe the backend JSON. Everything above the serializer uses
 * the UI shapes instead, so a backend change only touches `cost-values.ts` and
 * the serializers.
 */

// --- Raw API shapes -------------------------------------------------------

/**
 * One commission cell. `weekday: null` means simple mode; a number (ISO-8601
 * weekday, 1=Mon … 7=Sun) means advanced mode.
 */
export interface RawCostValue {
  leadtime_end: number | null;
  leadtime_start: number | null;
  /** A plain number is accepted on write; the API returns a 4-decimal string
   * (e.g. "14.0000") on read. */
  value: number | string;
  weekday: number | null;
}

export type CostValueType = 'currency' | 'percentage';

/** The $ / % toggle value, which is also what gets persisted. */
export type CommissionUnit = CostValueType;

export type CommissionMode = 'advanced' | 'simple';

export interface RawCost {
  /** Beta only writes "commission". */
  category: string;
  /** Beta only writes "room_night". */
  cost_basis: string;
  cost_values: RawCostValue[];
  id?: string;
  rate_structure: string;
  value_type: CostValueType;
}

export interface RawProfile {
  costs: RawCost[];
  created_at: string | null;
  description: string;
  id?: string;
  name: string | null;
  updated_at: string | null;
}

/** A PATCH only sends changed keys, so updates take a `Partial` of this. */
export type ProfileWritePayload = Omit<
  RawProfile,
  'created_at' | 'id' | 'updated_at'
>;

// --- UI-stable view model -------------------------------------------------

/** A lead-time range (null = "All (default)") with one value per weekday. */
export interface CostBookingWindowView {
  leadtimeEnd: number | null;
  leadtimeStart: number | null;
  values: (number | null)[];
}

/** Simple mode uses `value`; advanced mode uses `bookingWindows`. */
export interface CostValuesView {
  bookingWindows: CostBookingWindowView[];
  value: number | null;
}

/** Weekday columns in the advanced matrix (0=Mon … 6=Sun internally; the
 * backend's `RawCostValue.weekday` is ISO-8601 1=Mon … 7=Sun instead — see
 * `cost-values.ts` for the conversion). */
export const WEEKDAY_COUNT = 7;

export interface BookingWindowRow {
  id: string;
  label: string;
  values: (number | null)[];
}
